import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/* ───────────────────────────────────────────
   Landing Page — Sistema MED
   Mentor de Ética Digital
   Universidad de Caldas
   ─────────────────────────────────────────── */

// ─── Inline styles (no external dependencies) ───
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

  .landing-root {
    --color-primary: #1a6fa0;
    --color-primary-dark: #134f73;
    --color-primary-light: #e8f4fb;
    --color-accent: #0ea5a0;
    --color-accent-light: #e6f7f6;
    --color-warm: #f59e0b;
    --color-warm-light: #fef3c7;
    --color-danger: #dc2626;
    --color-danger-light: #fef2f2;
    --color-text: #1e293b;
    --color-text-secondary: #475569;
    --color-bg: #f8fafc;
    --color-white: #ffffff;
    --color-border: #e2e8f0;
    --font-display: 'DM Serif Display', Georgia, serif;
    --font-body: 'Source Sans 3', system-ui, sans-serif;
    font-family: var(--font-body);
    color: var(--color-text);
    background: var(--color-bg);
    overflow-x: hidden;
  }

  /* ── Animations ── */
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideInLeft {
    from { opacity: 0; transform: translateX(-40px); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }
  @keyframes pulse-ring {
    0% { transform: scale(0.9); opacity: 0.6; }
    100% { transform: scale(1.3); opacity: 0; }
  }
  @keyframes count-up {
    from { opacity: 0; transform: scale(0.5); }
    to { opacity: 1; transform: scale(1); }
  }

  .animate-fade-up {
    animation: fadeUp 0.7s ease-out forwards;
    opacity: 0;
  }
  .animate-fade-in {
    animation: fadeIn 0.6s ease-out forwards;
    opacity: 0;
  }
  .animate-slide-left {
    animation: slideInLeft 0.7s ease-out forwards;
    opacity: 0;
  }
  .delay-100 { animation-delay: 0.1s; }
  .delay-200 { animation-delay: 0.2s; }
  .delay-300 { animation-delay: 0.3s; }
  .delay-400 { animation-delay: 0.4s; }
  .delay-500 { animation-delay: 0.5s; }
  .delay-600 { animation-delay: 0.6s; }
  .delay-700 { animation-delay: 0.7s; }
  .delay-800 { animation-delay: 0.8s; }

  /* ── Hero ── */
  .hero-section {
    position: relative;
    min-height: 100vh;
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #0c3547 0%, var(--color-primary) 50%, #1a8a85 100%);
    overflow: hidden;
  }
  .hero-section::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(14, 165, 160, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 40%);
    pointer-events: none;
  }
  .hero-pattern {
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
    background-size: 60px 60px;
    pointer-events: none;
  }
  .hero-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
  }
  .hero-orb-1 {
    width: 400px; height: 400px;
    background: rgba(14, 165, 160, 0.12);
    top: -100px; right: -100px;
    animation: float 8s ease-in-out infinite;
  }
  .hero-orb-2 {
    width: 300px; height: 300px;
    background: rgba(245, 158, 11, 0.08);
    bottom: -50px; left: -50px;
    animation: float 10s ease-in-out infinite 2s;
  }

  /* ── Navigation ── */
  .landing-nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 50;
    transition: all 0.3s ease;
    padding: 1rem 0;
  }
  .landing-nav.scrolled {
    background: rgba(255, 255, 255, 0.97);
    backdrop-filter: blur(12px);
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
    padding: 0.5rem 0;
  }
  .landing-nav.scrolled .nav-logo-text,
  .landing-nav.scrolled .nav-link {
    color: var(--color-text) !important;
  }
  .landing-nav.scrolled .nav-btn-outline {
    border-color: var(--color-primary) !important;
    color: var(--color-primary) !important;
  }
  .nav-link {
    color: rgba(255,255,255,0.85);
    text-decoration: none;
    font-weight: 500;
    font-size: 0.95rem;
    transition: color 0.2s;
    cursor: pointer;
  }
  .nav-link:hover { color: #ffffff; }
  .landing-nav.scrolled .nav-link:hover { color: var(--color-primary) !important; }

  /* ── Buttons ── */
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 2rem;
    background: var(--color-accent);
    color: white;
    font-weight: 600;
    font-size: 1rem;
    border-radius: 10px;
    text-decoration: none;
    transition: all 0.25s ease;
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(14, 165, 160, 0.3);
  }
  .btn-primary:hover {
    background: #0c8c88;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(14, 165, 160, 0.4);
  }
  .btn-outline {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.875rem 2rem;
    background: transparent;
    color: white;
    font-weight: 600;
    font-size: 1rem;
    border-radius: 10px;
    border: 2px solid rgba(255,255,255,0.4);
    text-decoration: none;
    transition: all 0.25s ease;
    cursor: pointer;
  }
  .btn-outline:hover {
    background: rgba(255,255,255,0.1);
    border-color: white;
    transform: translateY(-2px);
  }
  .nav-btn-outline {
    padding: 0.5rem 1.25rem;
    border: 2px solid rgba(255,255,255,0.4);
    border-radius: 8px;
    color: white;
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
    transition: all 0.2s;
    display: inline-block;
  }
  .nav-btn-outline:hover {
    background: rgba(255,255,255,0.1);
    border-color: white;
  }
  .nav-btn-solid {
    padding: 0.5rem 1.25rem;
    background: var(--color-accent);
    border-radius: 8px;
    color: white;
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
    transition: all 0.2s;
    display: inline-block;
  }
  .nav-btn-solid:hover { background: #0c8c88; }

  /* ── Sections ── */
  .section {
    padding: 5rem 1.5rem;
  }
  .section-alt {
    background: var(--color-white);
  }
  .container {
    max-width: 1200px;
    margin: 0 auto;
  }
  .section-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 1rem;
    background: var(--color-primary-light);
    color: var(--color-primary);
    font-weight: 600;
    font-size: 0.85rem;
    border-radius: 100px;
    letter-spacing: 0.02em;
    margin-bottom: 1rem;
  }
  .section-title {
    font-family: var(--font-display);
    font-size: clamp(2rem, 4vw, 2.75rem);
    color: var(--color-text);
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  .section-subtitle {
    font-size: 1.125rem;
    color: var(--color-text-secondary);
    line-height: 1.7;
    max-width: 640px;
  }

  /* ── Module Cards ── */
  .module-card {
    background: var(--color-white);
    border-radius: 16px;
    padding: 2rem;
    border: 1px solid var(--color-border);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }
  .module-card::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: var(--color-primary);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  .module-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0,0,0,0.08);
    border-color: transparent;
  }
  .module-card:hover::after { transform: scaleX(1); }
  .module-icon {
    width: 52px;
    height: 52px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
  }

  /* ── Role Cards ── */
  .role-card {
    background: var(--color-white);
    border-radius: 16px;
    padding: 2rem;
    border: 1px solid var(--color-border);
    position: relative;
    overflow: hidden;
    transition: all 0.3s;
  }
  .role-card:hover {
    box-shadow: 0 8px 30px rgba(0,0,0,0.06);
  }
  .role-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 100px;
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 1rem;
  }

  /* ── Stats ── */
  .stat-card {
    text-align: center;
    padding: 2rem 1rem;
  }
  .stat-number {
    font-family: var(--font-display);
    font-size: 3rem;
    color: var(--color-primary);
    line-height: 1;
    margin-bottom: 0.5rem;
  }

  /* ── CTA Section ── */
  .cta-section {
    background: linear-gradient(135deg, #0c3547 0%, var(--color-primary) 50%, #1a8a85 100%);
    color: white;
    text-align: center;
    padding: 5rem 1.5rem;
    position: relative;
    overflow: hidden;
  }
  .cta-section::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: 
      linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
    background-size: 40px 40px;
  }

  /* ── Footer ── */
  .landing-footer {
    background: #0c2d3d;
    color: rgba(255,255,255,0.7);
    padding: 3rem 1.5rem 2rem;
  }
  .landing-footer a {
    color: rgba(255,255,255,0.7);
    text-decoration: none;
    transition: color 0.2s;
  }
  .landing-footer a:hover {
    color: white;
  }

  /* ── Responsive ── */
  .grid-modules {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
  }
  .grid-roles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.5rem;
  }
  .grid-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
  }
  .hero-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
  }
  @media (max-width: 768px) {
    .hero-content {
      grid-template-columns: 1fr;
      text-align: center;
    }
    .hero-buttons {
      justify-content: center;
    }
    .hero-visual {
      display: none;
    }
    .nav-links {
      display: none;
    }
    .section {
      padding: 3rem 1.25rem;
    }
  }

  /* ── Accessibility ── */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0,0,0,0);
    border: 0;
  }
  .landing-root *:focus-visible {
    outline: 3px solid var(--color-accent);
    outline-offset: 2px;
    border-radius: 4px;
  }

  /* ── Skip nav ── */
  .skip-nav {
    position: absolute;
    top: -100%;
    left: 1rem;
    padding: 0.75rem 1.5rem;
    background: var(--color-accent);
    color: white;
    font-weight: 600;
    border-radius: 0 0 8px 8px;
    z-index: 100;
    text-decoration: none;
    transition: top 0.2s;
  }
  .skip-nav:focus {
    top: 0;
  }

  /* ── Mobile menu ── */
  .mobile-menu-btn {
    display: none;
    background: none;
    border: none;
    color: white;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
  }
  @media (max-width: 768px) {
    .mobile-menu-btn { display: block; }
  }
  .mobile-menu {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(12, 53, 71, 0.98);
    z-index: 60;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
  }
  .mobile-menu.open {
    display: flex;
  }
  .mobile-menu a {
    color: white;
    font-size: 1.25rem;
    text-decoration: none;
    font-weight: 500;
  }
`;

// ─── Data ───
const modules = [
  {
    icon: '📋',
    title: 'Lista de Chequeo',
    description: '17 ítems en 4 dimensiones éticas. Evalúa riesgos en privacidad, algoritmos, impacto social y gobernanza con análisis automático.',
    color: '#1a6fa0',
    bg: '#e8f4fb',
  },
  {
    icon: '📊',
    title: 'Encuestas Éticas',
    description: '12 preguntas estandarizadas con matriz de cualificación. Mide el nivel ético organizacional con resultados agregados.',
    color: '#7c3aed',
    bg: '#f3f0ff',
  },
  {
    icon: '📜',
    title: 'Códigos de Ética',
    description: 'Generación automática de códigos personalizados basados en tus evaluaciones. Con plantillas, aprobación y versionado.',
    color: '#0ea5a0',
    bg: '#e6f7f6',
  },
  {
    icon: '👥',
    title: 'Comité de Ética',
    description: 'Gestión del comité con roles de Presidente, Secretario y Miembro. Asignación de casos y seguimiento de actividades.',
    color: '#f59e0b',
    bg: '#fef3c7',
  },
  {
    icon: '🔒',
    title: 'Denuncias',
    description: 'Canal confidencial para reportes anónimos o identificados. 7 categorías, 3 niveles de prioridad y timeline de seguimiento.',
    color: '#dc2626',
    bg: '#fef2f2',
  },
  {
    icon: '📖',
    title: 'Glosario Ético',
    description: 'Consulta de términos éticos con definiciones detalladas, categorías, búsqueda avanzada y navegación alfabética.',
    color: '#059669',
    bg: '#ecfdf5',
  },
];

const roles = [
  {
    title: 'SuperAdmin',
    badge: 'Administración Total',
    badgeColor: '#dc2626',
    badgeBg: '#fef2f2',
    description: 'Gestión global del sistema: organizaciones, usuarios de todas las unidades y configuración institucional.',
    permissions: ['Gestión de organizaciones', 'Todos los usuarios', 'Configuración global'],
  },
  {
    title: 'Admin de Organización',
    badge: 'Gestión por Facultad',
    badgeColor: '#1a6fa0',
    badgeBg: '#e8f4fb',
    description: 'Coordinadores y directores que gestionan usuarios, comité, encuestas y códigos de ética de su unidad.',
    permissions: ['Usuarios de su unidad', 'Comité de ética', 'Generar códigos'],
  },
  {
    title: 'Miembro',
    badge: 'Participación Activa',
    badgeColor: '#0ea5a0',
    badgeBg: '#e6f7f6',
    description: 'Docentes, estudiantes y empleados que participan en evaluaciones, consultan el glosario y reportan denuncias.',
    permissions: ['Evaluaciones y encuestas', 'Consultar glosario', 'Crear denuncias'],
  },
];

// ─── Helper: scroll to section ───
const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// ─── Intersection Observer hook ───
const useInView = (options = {}) => {
  const [ref, setRef] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { threshold: 0.15, ...options });
    observer.observe(ref);
    return () => observer.disconnect();
  }, [ref, options]);

  return [setRef, isVisible];
};

// ─── Section wrapper with scroll animation ───
const AnimatedSection = ({ children, className = '' }) => {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={className} style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.7s ease-out' }}>
      {children}
    </div>
  );
};

// ─── Component ───
const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: 'Módulos', id: 'modulos' },
    { label: 'Roles', id: 'roles' },
    { label: 'Beneficios', id: 'beneficios' },
    { label: 'Soporte', id: 'soporte' },
  ];

  return (
    <div className="landing-root">
      <style>{styles}</style>

      {/* Skip navigation for accessibility */}
      <a href="#main-content" className="skip-nav">Saltar al contenido principal</a>

      {/* ── Navigation ── */}
      <nav className={`landing-nav ${scrolled ? 'scrolled' : ''}`} role="navigation" aria-label="Navegación principal">
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '1rem' }}>M</div>
            <span className="nav-logo-text" style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', color: 'white' }}>Sistema MED</span>
          </div>

          <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
            {navItems.map((item) => (
              <button key={item.id} className="nav-link" onClick={() => scrollTo(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>
                {item.label}
              </button>
            ))}
            <Link to="/login" className="nav-btn-outline">Iniciar Sesión</Link>            
          </div>

          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú" style={{ color: scrolled ? 'var(--color-text)' : 'white' }}>
            ☰
          </button>
        </div>
      </nav>

      {/* ── Mobile Menu ── */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} role="dialog" aria-label="Menú de navegación">
        <button onClick={() => setMobileMenuOpen(false)} style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'none', border: 'none', color: 'white', fontSize: '2rem', cursor: 'pointer' }} aria-label="Cerrar menú">✕</button>
        {navItems.map((item) => (
          <button key={item.id} onClick={() => { scrollTo(item.id); setMobileMenuOpen(false); }} style={{ background: 'none', border: 'none', color: 'white', fontSize: '1.25rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            {item.label}
          </button>
        ))}
        <Link to="/login" onClick={() => setMobileMenuOpen(false)} style={{ color: 'white', fontSize: '1.25rem' }}>Iniciar Sesión</Link>
        <Link to="/register" className="nav-btn-solid" onClick={() => setMobileMenuOpen(false)}>Registrarse</Link>
      </div>

      {/* ── Hero ── */}
      <header className="hero-section" id="main-content">
        <div className="hero-pattern" />
        <div className="hero-orb hero-orb-1" />
        <div className="hero-orb hero-orb-2" />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="hero-content" style={{ padding: '6rem 1.5rem 4rem' }}>
            <div>
              <div className="animate-fade-up" style={{ marginBottom: '1.5rem' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 1rem', background: 'rgba(14, 165, 160, 0.15)', color: '#7eeee9', borderRadius: '100px', fontSize: '0.9rem', fontWeight: 500 }}>
                  🏛️ Universidad de Caldas
                </span>
              </div>
              <h1 className="animate-fade-up delay-100" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', color: 'white', lineHeight: 1.15, marginBottom: '1.5rem' }}>
                Mentor de<br />Ética Digital
              </h1>
              <p className="animate-fade-up delay-200" style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, maxWidth: 520, marginBottom: '2.5rem' }}>
                Plataforma integral para gestionar, evaluar y promover prácticas éticas en la comunidad universitaria. Evaluaciones automatizadas, códigos de ética personalizados y un canal seguro de denuncias.
              </p>
              <div className="animate-fade-up delay-300 hero-buttons" style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <Link to="/login" className="btn-primary">
                  Comenzar ahora →
                </Link>
                <button onClick={() => scrollTo('modulos')} className="btn-outline">
                  Conocer módulos
                </button>
              </div>
            </div>

            {/* Hero visual */}
            <div className="hero-visual animate-fade-in delay-400" style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={{ position: 'relative' }}>
                {/* Decorative rings */}
                <div style={{ position: 'absolute', inset: -20, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)' }} />
                <div style={{ position: 'absolute', inset: -50, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.04)' }} />
                {/* Central element */}
                <div style={{ width: 320, height: 320, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
                  <div style={{ fontSize: '4rem' }}>⚖️</div>
                  <div style={{ color: 'white', fontFamily: 'var(--font-display)', fontSize: '1.5rem' }}>MED</div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>v1.0 — 2025</div>
                </div>
                {/* Floating icons */}
                {[
                  { emoji: '📋', top: '10%', left: '-15%', delay: '0s' },
                  { emoji: '🔒', top: '70%', left: '-10%', delay: '1s' },
                  { emoji: '📊', top: '5%', right: '-10%', delay: '2s' },
                  { emoji: '📜', top: '75%', right: '-15%', delay: '0.5s' },
                ].map((item, i) => (
                  <div key={i} style={{ position: 'absolute', ...item, width: 48, height: 48, borderRadius: 12, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', animation: `float 6s ease-in-out infinite ${item.delay}` }}>
                    {item.emoji}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ── Stats ── */}
      <section style={{ background: 'var(--color-white)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="container">
          <AnimatedSection>
            <div className="grid-stats" style={{ padding: '3rem 1.5rem' }}>
              {[
                { number: '7', label: 'Módulos integrados' },
                { number: '3', label: 'Niveles de acceso' },
                { number: '17', label: 'Ítems de evaluación' },
                { number: '12', label: 'Preguntas de encuesta' },
                { number: '7', label: 'Tipos de denuncia' },
              ].map((stat, i) => (
                <div key={i} className="stat-card">
                  <div className="stat-number">{stat.number}</div>
                  <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem', fontWeight: 500 }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Módulos ── */}
      <section className="section" id="modulos">
        <div className="container">
          <AnimatedSection>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <span className="section-badge">⚡ Funcionalidades</span>
              <h2 className="section-title">Todo lo que necesitas en un solo lugar</h2>
              <p className="section-subtitle" style={{ margin: '0 auto' }}>
                Seis módulos diseñados para cubrir cada aspecto de la gestión ética institucional, desde la evaluación hasta el seguimiento de casos.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid-modules">
            {modules.map((mod, i) => (
              <AnimatedSection key={i}>
                <div className="module-card" style={{ height: '100%' }}>
                  <div className="module-icon" style={{ background: mod.bg, color: mod.color }}>{mod.icon}</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', marginBottom: '0.75rem' }}>{mod.title}</h3>
                  <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.7, fontSize: '0.95rem' }}>{mod.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Roles ── */}
      <section className="section section-alt" id="roles">
        <div className="container">
          <AnimatedSection>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <span className="section-badge">👤 Roles y Permisos</span>
              <h2 className="section-title">Acceso adaptado a cada perfil</h2>
              <p className="section-subtitle" style={{ margin: '0 auto' }}>
                Tres niveles de acceso que garantizan que cada usuario vea exactamente lo que necesita, ni más ni menos.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid-roles">
            {roles.map((role, i) => (
              <AnimatedSection key={i}>
                <div className="role-card" style={{ height: '100%' }}>
                  <span className="role-badge" style={{ color: role.badgeColor, background: role.badgeBg }}>{role.badge}</span>
                  <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.35rem', marginBottom: '0.75rem' }}>{role.title}</h3>
                  <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.7, fontSize: '0.95rem', marginBottom: '1.25rem' }}>{role.description}</p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {role.permissions.map((perm, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
                        <span style={{ color: 'var(--color-accent)', fontWeight: 700 }}>✓</span> {perm}
                      </li>
                    ))}
                  </ul>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── Beneficios ── */}
      <section className="section" id="beneficios">
        <div className="container">
          <AnimatedSection>
            <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
              <span className="section-badge">✨ Beneficios</span>
              <h2 className="section-title">¿Por qué usar el Sistema MED?</h2>
            </div>
          </AnimatedSection>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '2rem' }}>
            {[
              { icon: '🎯', title: 'Evaluación Estructurada', text: 'Herramientas estandarizadas con 4 dimensiones éticas y niveles de riesgo automáticos para medir el estado ético real.' },
              { icon: '🤖', title: 'Automatización Inteligente', text: 'Códigos de ética generados automáticamente a partir de tus evaluaciones. Plantillas personalizables y versionado.' },
              { icon: '🔒', title: 'Confidencialidad Total', text: 'Denuncias anónimas que protegen completamente la identidad. Solo el comité de ética tiene acceso a los casos.' },
              { icon: '📊', title: 'Transparencia', text: 'Seguimiento completo de casos con timeline de acciones. Resultados agregados y reportes descargables en PDF.' },
              { icon: '📱', title: 'Acceso Universal', text: 'Diseño responsive que funciona en escritorio, tablet y móvil. Compatible con Chrome, Firefox, Edge y Safari.' },
              { icon: '🏛️', title: 'Respaldo Institucional', text: 'Desarrollado para la Universidad de Caldas por la Facultad de Inteligencia Artificial e Ingenierías.' },
            ].map((item, i) => (
              <AnimatedSection key={i}>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                  <div style={{ fontSize: '1.75rem', flexShrink: 0, marginTop: 2 }}>{item.icon}</div>
                  <div>
                    <h3 style={{ fontWeight: 600, fontSize: '1.05rem', marginBottom: '0.5rem' }}>{item.title}</h3>
                    <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.7, fontSize: '0.93rem' }}>{item.text}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="cta-section">
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <AnimatedSection>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 4vw, 2.75rem)', marginBottom: '1rem' }}>
              Comienza a fortalecer la ética en tu organización
            </h2>
            <p style={{ fontSize: '1.15rem', opacity: 0.85, maxWidth: 560, margin: '0 auto 2rem', lineHeight: 1.7 }}>
              Regístrate hoy y accede a todas las herramientas de evaluación, generación de códigos y gestión de denuncias.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/register" className="btn-primary" style={{ background: 'white', color: 'var(--color-primary)', boxShadow: '0 4px 14px rgba(0,0,0,0.15)' }}>
                Crear cuenta gratuita →
              </Link>
              <Link to="/login" className="btn-outline">
                Ya tengo cuenta
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Soporte ── */}
      <section className="section section-alt" id="soporte">
        <div className="container">
          <AnimatedSection>
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
              <span className="section-badge">💬 Soporte</span>
              <h2 className="section-title">¿Necesitas ayuda?</h2>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', maxWidth: 800, margin: '0 auto' }}>
              <div style={{ background: 'var(--color-bg)', borderRadius: 16, padding: '2rem', textAlign: 'center', border: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📧</div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Correo electrónico</h3>
                <a href="mailto:mlopez@ucaldas.edu.co" style={{ color: 'var(--color-primary)', fontWeight: 500, textDecoration: 'none' }}>
                  mlopez@ucaldas.edu.co
                </a>
              </div>
              <div style={{ background: 'var(--color-bg)', borderRadius: 16, padding: '2rem', textAlign: 'center', border: '1px solid var(--color-border)' }}>
                <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🕐</div>
                <h3 style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Horario de atención</h3>
                <p style={{ color: 'var(--color-text-secondary)' }}>Lunes a Viernes<br />8:00 AM — 5:00 PM</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="landing-footer" role="contentinfo">
        <div className="container">
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: 'var(--color-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: '0.85rem' }}>M</div>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'white' }}>Sistema MED</span>
              </div>
              <p style={{ fontSize: '0.9rem', maxWidth: 360, lineHeight: 1.6 }}>
                Mentor de Ética Digital — Plataforma de gestión ética para la Universidad de Caldas.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: 'white', fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.9rem' }}>Plataforma</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <Link to="/login" style={{ fontSize: '0.9rem' }}>Iniciar Sesión</Link>
                  <Link to="/register" style={{ fontSize: '0.9rem' }}>Registrarse</Link>
                </div>
              </div>
              <div>
                <div style={{ color: 'white', fontWeight: 600, marginBottom: '0.75rem', fontSize: '0.9rem' }}>Institución</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <a href="https://www.ucaldas.edu.co" target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.9rem' }}>Universidad de Caldas</a>
                  <span style={{ fontSize: '0.9rem' }}>Facultad IA e Ingenierías</span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '1.5rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.85rem' }}>
            <span>© {new Date().getFullYear()} Universidad de Caldas — Todos los derechos reservados</span>
            <span>Sistema MED v1.0</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
